import { ADMIN_API_PREFIX, fillPath, route } from "./routes.js";
import { z } from "zod";
import { OIDC_ACCEPT_MAX_ENTRIES, PERMISSION_KEY_PATTERN, externalIssuerSchema, issuerNamesPrincipal, oidcIssuerSchema } from "@zion/sdk-contracts";
//#region src/machine-trust-policies.ts
/**
* `/v1/admin/machine-trust-policies` — the org's trust policies: which OIDC assertion may become
* which `external` machine principal (feature 2026-10-01-dp-oidc-federation, D-011).
*
* Producer: the WS-5 admin controller under `cloud/dp/core-api/src/admin/machine-trust-policies/`.
* Consumer: the org admin app's client. The exchange at `POST /v1/auth/token/federated`
* (`FEDERATED_TOKEN_PATH` in `@zion/sdk-contracts`) reads the rows these routes write: it matches
* a presented assertion's `(iss, sub, claims)` against one enabled row and refuses on zero matches
* and on two (DF-3).
*
* A row's match key, `(issuer, subject, requiredClaims)`, is immutable after create. The only
* update is the `disabled` switch. A different match key is a new row, so an audit row that names
* a policy id names the exact key that matched at the time.
*
* Permissions (R-015): the class carries `settings:identity:manage`; the list handler narrows to
* `settings:identity:read`. `NoDelegatedPrincipalGuard` sits on the class, as on
* `AdminOAuthClientsController`, so a token carrying `scp` is refused at every route here. A create
* and a re-enable resolve the principal inside the caller's org under RLS, so another org's
* principal is a 404. The no-escalation check below is the set's fourth guard (R-017).
*
* No escalation through a policy (R-017). A policy lets whoever controls its issuer log in as the
* principal, so writing one hands the caller a credential carrying everything the principal can
* do. {@link CREATE_MACHINE_TRUST_POLICY_ROUTE}, and {@link UPDATE_MACHINE_TRUST_POLICY_ROUTE} when
* it re-enables a row, answer 403 unless the caller could confer the principal's whole effective
* authority: the roles it holds directly, the roles of every group it belongs to, and the resource
* grants whose subject is the principal, one of those groups or one of those roles. A holder of
* `settings:identity:manage` cannot bind an issuer they control to a principal an admin created,
* put in a powerful group or gave a role that carries grants.
* `MACHINE_PRINCIPAL_ESCALATION_CHECKED_ROUTES` in `machine-principals.ts` defines the check and
* lists both; `machinePrincipalEscalationScope` there computes what it covers.
*
* Audit (DF-12): create writes `machine_trust_policy.created`; a patch that moves `disabled` from
* false to true writes `machine_trust_policy.disabled`; a patch that moves it from true to false
* writes `machine_trust_policy.updated`. A patch that sets the value the row already holds changes
* nothing and writes no row.
*
* App binding (feature 2026-10-11-app-oidc, D9). A policy may bind to one app with `appId` and a
* subset of that app's `capabilities.oidc.accept[]` permissions with `appPermissions`. The exchange
* then mints a token whose only authority is those permissions of that app, and it refuses while
* the app's `oidc` enablement is off. A policy with no binding mints the principal's role authority,
* as before. The two members travel together: both or neither.
*
* Direction rule (R-28): every response schema here is `z.object`, so a member core-api adds is
* stripped and cannot red a page. Every request schema is `z.strictObject`, so a key the server
* would reject is refused before the request leaves the client. No schema here carries a
* `.transform()`, so `createOpenApiDocument()` can represent every one.
*/
/** The collection path. No parameter, so the OpenAPI form and the request form are the same. */
const ADMIN_MACHINE_TRUST_POLICIES_PATH = `${ADMIN_API_PREFIX}/machine-trust-policies`;
/** One policy, in OpenAPI `{param}` form. Fill it with {@link adminMachineTrustPolicyPath}. */
const ADMIN_MACHINE_TRUST_POLICY_PATH_TEMPLATE = `${ADMIN_MACHINE_TRUST_POLICIES_PATH}/{id}`;
/**
* GitHub Actions' OIDC issuer, verbatim. A policy for this issuer, or for a GitHub Enterprise
* Cloud issuer under it (`https://token.actions.githubusercontent.com/<enterprise slug>`), must pin
* {@link GITHUB_REQUIRED_CLAIM_KEY}: the GitHub `sub` pins the repository and the ref, never the
* workflow file, so without it any workflow in the repository matches. The control plane's
* `machine_trust_policies_github_requires_job_workflow_ref` CHECK carries the rule for the public
* issuer only.
*/
const GITHUB_ACTIONS_ISSUER = "https://token.actions.githubusercontent.com";
/** The claim a policy for {@link GITHUB_ACTIONS_ISSUER} must name in `requiredClaims`. */
const GITHUB_REQUIRED_CLAIM_KEY = "job_workflow_ref";
/**
* The longest issuer a policy stores, in characters: 2,048, the bound `oidcIssuerSchema` in
* `@zion/sdk-contracts` applies (R-001). A test pins this value against the rendered JSON Schema
* `maxLength` of {@link trustPolicyIssuerSchema}, so the two never disagree.
*/
const TRUST_POLICY_ISSUER_MAX_LENGTH = 2048;
/** The longest subject a policy stores. */
const TRUST_POLICY_SUBJECT_MAX_LENGTH = 512;
/** The longest `requiredClaims` object, measured as its `JSON.stringify` length. */
const TRUST_POLICY_REQUIRED_CLAIMS_MAX_JSON_LENGTH = 1024;
/**
* The most `appPermissions` one binding names: `OIDC_ACCEPT_MAX_ENTRIES` of `@zion/sdk-contracts`,
* 32, because each permission must appear in one of the app's `accept[]` entries.
*/
const TRUST_POLICY_APP_PERMISSIONS_MAX = OIDC_ACCEPT_MAX_ENTRIES;
/**
* The permissions a policy bound to an app confers (app-oidc, D9): 1 to
* {@link TRUST_POLICY_APP_PERMISSIONS_MAX} unique app permission keys (`PERMISSION_KEY_PATTERN`).
* The route refuses with 400 a key that no `capabilities.oidc.accept[]` entry of the app declares;
* this schema knows no declaration.
*/
const trustPolicyAppPermissionsSchema = z.array(z.string().regex(PERMISSION_KEY_PATTERN, "appPermissions: an app permission key")).min(1).max(TRUST_POLICY_APP_PERMISSIONS_MAX).refine((keys) => new Set(keys).size === keys.length, { message: "appPermissions are unique" });
/** The message of the issue a body with one of `appId` and `appPermissions` raises. */
const APP_BINDING_PAIR_MESSAGE = "appId and appPermissions travel together: send both or neither";
/**
* Add one issue when exactly one of `appId` and `appPermissions` is present, on the absent member.
* Shared by the create and the patch body.
*/
function refuseHalfBinding(body, ctx) {
	if (body.appId !== void 0 && body.appPermissions === void 0) ctx.addIssue({
		code: "custom",
		message: APP_BINDING_PAIR_MESSAGE,
		path: ["appPermissions"]
	});
	if (body.appId === void 0 && body.appPermissions !== void 0) ctx.addIssue({
		code: "custom",
		message: APP_BINDING_PAIR_MESSAGE,
		path: ["appId"]
	});
}
/**
* The issuer a policy names, compared byte for byte with the assertion's `iss`: the schema never
* rewrites it, so `https://idp.example` and `https://idp.example/` are two different issuers and a
* policy matches only the one it stores. This is `oidcIssuerSchema` from `@zion/sdk-contracts`,
* under the name this family has always published (R-001, R-020). It admits both issuer kinds a
* policy can name:
*
* - a discovered OIDC issuer, held to `oidcIssuerSchema`: GitHub Actions, Google, a GKE or EKS
*   cluster, Auth0, Okta, any IdP that publishes `/.well-known/openid-configuration`;
* - a registered-key issuer, `https://<org host>/machine-principals/<principal uuid>`, held to
*   `externalIssuerSchema`, a form `oidcIssuerSchema` admits in full. The create body tells the two
*   kinds apart with `externalIssuerSchema` (R-020).
*
* Valid: an `https` URL of at most {@link TRUST_POLICY_ISSUER_MAX_LENGTH} characters whose host is
* a lower-case DNS name of at most 253 characters (each label 1 to 63 letters, digits or hyphens
* with no edge hyphen), with an optional port of 1 to 65535 and a path of non-empty segments, that
* `new URL(issuer).href` gives back unchanged, with only the `/` the parser appends to a bare
* origin. A trailing slash is valid, because Auth0 and Azure AD v1 put one in `iss`; an upper-case
* path is valid, because EKS issuers carry one.
*
* Refused (DF-3): `http://`; user info; a query or a fragment; whitespace or a backslash; an
* upper-case host, an empty or hyphen-edged label, a port of 0, with a leading zero or the default
* `:443`, a port above 65535, an empty inner path segment, a `.` or `..` path segment, a shorthand
* IPv4 host such as `127.1`, or any other spelling the URL parser rewrites; an IPv4 or IPv6 literal
* host. A rewritten spelling is a different string to `=`, so a policy that stored one would never
* match and would look like a policy that does nothing.
*
* Reachability is the route's check, not this schema's (R-020): a single-label host such as
* `https://localhost` and a reserved suffix such as `.internal` or `.svc` parse here, and
* {@link CREATE_MACHINE_TRUST_POLICY_ROUTE} refuses them with `refuseUndiscoverableIssuer` from
* `@zion/platform-identity`, the predicate the exchange applies.
*
* The create body adds the rules that depend on the other members: see
* {@link createMachineTrustPolicyRequestSchema}.
*/
const trustPolicyIssuerSchema = oidcIssuerSchema;
/**
* The subject a policy names, compared for exact equality with the assertion's `sub`. Never a
* prefix, a glob or a `LIKE`.
*
* Valid: 1 to {@link TRUST_POLICY_SUBJECT_MAX_LENGTH} printable ASCII characters (`!` to `~`)
* other than `*` and `%`. Refused: `*` and `%`, the two characters that only ever mean wildcard,
* so no stored subject can be read as a pattern; whitespace; control characters; any character
* outside ASCII. A GitHub `sub` (`repo:acme/infra:ref:refs/heads/main`), a GCP numeric `sub` and a
* Kubernetes `system:serviceaccount:ns:name` all fit.
*/
const trustPolicySubjectSchema = z.string().min(1).max(512).regex(/^[!-$&-)+-~]+$/, "subject: printable ASCII with no *, %, whitespace or control character");
/**
* A flat map of non-empty string claim keys to non-empty string values, at most
* {@link TRUST_POLICY_REQUIRED_CLAIMS_MAX_JSON_LENGTH} characters as `JSON.stringify` writes it. It
* may be empty; whether it must name a claim depends on the issuer, which the create body decides.
* The three prototype names are core-api's to refuse, before validation (R-021, R-033).
*/
const requiredClaimsMapSchema = z.record(z.string().min(1, "claim key must not be empty"), z.string().min(1, "claim value must not be empty")).refine((claims) => JSON.stringify(claims).length <= TRUST_POLICY_REQUIRED_CLAIMS_MAX_JSON_LENGTH, { message: `requiredClaims must serialise to at most ${TRUST_POLICY_REQUIRED_CLAIMS_MAX_JSON_LENGTH} characters` });
/** The message of the issue a discovered-issuer policy with no required claim raises. */
const EMPTY_CLAIMS_MESSAGE = "requiredClaims must name at least one claim: the subject alone is too coarse a trust boundary";
/**
* The claims an assertion from a discovered OIDC issuer must carry, each equal to the value here.
*
* Valid: a flat object of 1 or more entries whose every key and every value is a non-empty
* string, at most {@link TRUST_POLICY_REQUIRED_CLAIMS_MAX_JSON_LENGTH} characters as
* `JSON.stringify` writes it. Refused: the empty object, because for a discovered issuer "the
* subject alone" is too coarse a trust boundary (#1644 §4.3, DF-3); a nested object, an array, a
* number or a boolean value, because the exchange's comparison is string equality and has no
* meaning for them; an empty key or value.
*
* A policy whose issuer is its principal's own registered-key issuer may carry `{}` (R-013);
* {@link createMachineTrustPolicyRequestSchema} applies that exemption, so this schema is the rule
* for every other issuer.
*
* The claim keys `__proto__`, `constructor` and `prototype` are refused in one place: core-api scans
* the raw body's `requiredClaims` own keys before validation and answers 400 on any of the three
* (R-021, R-033). This schema adds no check for them. zod 4 drops a `__proto__` key before any key
* check runs, so it parses as absent; `constructor` and `prototype` parse as ordinary claim keys.
* A client that validates with this schema alone can therefore send a body the server refuses.
*/
const trustPolicyRequiredClaimsSchema = requiredClaimsMapSchema.refine((claims) => Object.keys(claims).length > 0, { message: EMPTY_CLAIMS_MESSAGE });
/**
* One row of every route here. The control plane's `MachineTrustPolicySummary` spelling (DF-2).
*
* - `id`: the `auth.machine_trust_policies` uuid.
* - `machinePrincipalId`: the `external` principal an assertion matching this row becomes.
* - `issuer`, `subject`, `requiredClaims`: the match key, as stored.
* - `disabled`: true when the exchange skips this row.
* - `appId`, `appPermissions`: the app binding (app-oidc, D9), both null on an unbound row. Optional,
*   so a client parses a row from a core-api that predates the binding; absent reads as unbound.
* - `createdAt`, `updatedAt`: ISO 8601 UTC; `updatedAt` moves on each `disabled` change.
*/
const adminMachineTrustPolicySchema = z.object({
	id: z.uuid(),
	machinePrincipalId: z.uuid(),
	issuer: z.string(),
	subject: z.string(),
	requiredClaims: z.record(z.string(), z.string()),
	disabled: z.boolean(),
	appId: z.uuid().nullable().optional(),
	appPermissions: z.array(z.string()).nullable().optional(),
	createdAt: z.iso.datetime(),
	updatedAt: z.iso.datetime()
});
/**
* `GET /v1/admin/machine-trust-policies`. Every policy of the caller's org, enabled and disabled,
* unpaged. Status 200. Permission `settings:identity:read`.
*/
const LIST_MACHINE_TRUST_POLICIES_ROUTE = route("GET", ADMIN_MACHINE_TRUST_POLICIES_PATH);
/**
* `POST /v1/admin/machine-trust-policies`. Creates one enabled policy. Status 201 with the row.
* 400 in six cases, each decided before the principal is read, so a 400 says nothing about it:
*
* - the body {@link createMachineTrustPolicyRequestSchema} refuses, the registered-key arm's
*   issuer and subject rules included (R-023);
* - the raw body's `requiredClaims` names `__proto__`, `constructor` or `prototype` as an own key,
*   found by a scan of the raw JSON before validation (R-021, R-033). The schema does not refuse
*   them, and zod drops `__proto__` unseen, so the scan is the only check;
* - `refuseUndiscoverableIssuer` from `@zion/platform-identity` refuses the issuer: a single-label
*   host or a reserved suffix, which the exchange would refuse on every presentation (R-020);
* - an issuer of the registered-key form (`externalIssuerSchema`) does not start with the plane's
*   registered-key prefix, `https://<host>/machine-principals/` where `<host>` is the host and
*   port of the plane's issuer record as `registeredKeyIssuer()` builds it (R-001): a different
*   host or a different port. Only the plane's own registered keys verify under that form, and a
*   port-only difference would store `{}` claims for an issuer the exchange treats as discovered;
* - an issuer starts with the plane's registered-key prefix and `parseRegisteredKeyIssuer` from
*   `@zion/platform-identity` reads no principal from it, such as a trailing slash, an upper-case
*   uuid, an extra segment or the bare prefix. The exchange refuses every presentation of such an
*   issuer with `issuer_not_allowed` (DF-3).
* - the issuer host resolves only to private, loopback, link-local, CGNAT or unspecified addresses,
*   found by `refuseUnreachableIssuer` from `@zion/platform-identity` with one `dns.lookup` at save
*   time (R-053): `issuer: the issuer host does not resolve to a public address`.
*
* 404 when
* `machinePrincipalId` names no principal, a principal of another org, or a principal whose type
* is not `external`, so the response is no cross-tenant existence oracle and no outside issuer can
* become a compute unit. 403 when the caller could not confer the principal's whole effective
* authority (its direct roles, the roles of every group it belongs to, and the resource grants
* whose subject is the principal, one of those groups or one of those roles), the check
* `MACHINE_PRINCIPAL_ESCALATION_CHECKED_ROUTES` in `machine-principals.ts` defines (R-017), run
* once the principal resolves and before any write; 403
* outranks 409, so a caller who fails it learns nothing about the principal's policies. 409 when
* the principal is revoked, and when the org already holds a policy, enabled or disabled, with
* the same match key: re-enable that row with a patch.
* Permission `settings:identity:manage`, `NoDelegatedPrincipalGuard`, the principal resolved inside
* the caller's org under RLS (R-015), and the 403 check as the fourth guard (R-017). Audit
* `machine_trust_policy.created`.
*/
const CREATE_MACHINE_TRUST_POLICY_ROUTE = route("POST", ADMIN_MACHINE_TRUST_POLICIES_PATH);
/**
* `PATCH /v1/admin/machine-trust-policies/{id}`. Enables or disables one policy. Status 200 with
* the row. 404 for an unknown id and for another org's id. A body of `{ disabled: false }` answers
* 403 when the caller could not confer the whole effective authority of the row's principal (its
* direct roles, the roles of every group it belongs to, and the resource grants whose subject is
* the principal, one of those groups or one of those roles), the check
* {@link CREATE_MACHINE_TRUST_POLICY_ROUTE} makes (R-017), because re-enabling a row makes its
* issuer a credential for the principal again; the check runs on every such body, a row already enabled
* included, before any write. `{ disabled: true }` takes the principal's credential away and runs
* no such check. Permission `settings:identity:manage` and `NoDelegatedPrincipalGuard` on every
* body, the row and its principal resolved inside the caller's org under RLS (R-015: a re-enable is
* a grant, so it carries the create's guard set).
*/
const UPDATE_MACHINE_TRUST_POLICY_ROUTE = route("PATCH", ADMIN_MACHINE_TRUST_POLICY_PATH_TEMPLATE);
/**
* Fill {@link ADMIN_MACHINE_TRUST_POLICY_PATH_TEMPLATE} with one policy id.
*
* @param policyId the policy uuid, as {@link adminMachineTrustPolicySchema} `id` publishes it.
* @returns the request path.
* @throws a `ZodError` when `policyId` is not a uuid, so a slash, a dot-segment or an empty
*   string never reaches a URL.
*/
function adminMachineTrustPolicyPath(policyId) {
	return fillPath(ADMIN_MACHINE_TRUST_POLICY_PATH_TEMPLATE, { id: policyId }, { id: z.uuid() });
}
/** `GET /v1/admin/machine-trust-policies` response: a bare array of rows. */
const listMachineTrustPoliciesResponseSchema = z.array(adminMachineTrustPolicySchema);
/**
* True when `issuer` is the GitHub Actions issuer or a GitHub Enterprise Cloud issuer under it.
*
* @param issuer an issuer {@link trustPolicyIssuerSchema} accepted.
*/
function isGitHubActionsIssuer(issuer) {
	return issuer === "https://token.actions.githubusercontent.com" || issuer.startsWith(`https://token.actions.githubusercontent.com/`);
}
/**
* `POST /v1/admin/machine-trust-policies` body.
*
* - `machinePrincipalId`: a uuid; the route answers 404 unless it names an `external` principal
*   of the caller's org.
* - `issuer`: {@link trustPolicyIssuerSchema}.
* - `subject`: {@link trustPolicySubjectSchema}.
* - `requiredClaims`: a flat string map of at most
*   {@link TRUST_POLICY_REQUIRED_CLAIMS_MAX_JSON_LENGTH} serialised characters, with the issuer
*   rules below.
*
* Two issuer kinds, told apart by `externalIssuerSchema` from `@zion/sdk-contracts` (R-020):
*
* 1. A registered-key issuer, `https://<org host>/machine-principals/<uuid>` (R-001, R-023). Its uuid must
*    be `machinePrincipalId`, lower-case, by `issuerNamesPrincipal` from `@zion/sdk-contracts`
*    (R-029), or the body is refused with the issue on `issuer`: a
*    policy that let one principal's key log in as another would be a second identity for the key.
*    Its `subject` must be that same lower-case uuid, or the body is refused with the issue on
*    `subject`: the machine signs its own principal id as `sub`, so any other subject never
*    matches. Its `requiredClaims` may be `{}`, because the registered key binds the identity
*    (R-013).
* 2. Any other issuer is a discovered OIDC issuer. Its `requiredClaims` must name at least one
*    claim, with the issue on `requiredClaims` (R-013, DF-3). When the issuer is
*    {@link GITHUB_ACTIONS_ISSUER} or an issuer under it followed by `/`, `requiredClaims` must name
*    {@link GITHUB_REQUIRED_CLAIM_KEY}, with the issue on `requiredClaims`.
*
* Optional app binding (app-oidc, D9): `appId`, a uuid of an app in the org's catalog, and
* `appPermissions`, {@link trustPolicyAppPermissionsSchema}. Both or neither, with the issue on the
* absent member. The route answers 404 when `appId` names no app in the caller's org catalog, and
* 400 when a permission appears in no `capabilities.oidc.accept[]` entry of that app.
*
* Refused as well: a `disabled` key, because a policy is created enabled; any other key.
*/
const createMachineTrustPolicyRequestSchema = z.strictObject({
	machinePrincipalId: z.uuid(),
	issuer: trustPolicyIssuerSchema,
	subject: trustPolicySubjectSchema,
	requiredClaims: requiredClaimsMapSchema,
	appId: z.uuid().optional(),
	appPermissions: trustPolicyAppPermissionsSchema.optional()
}).superRefine((body, ctx) => {
	refuseHalfBinding(body, ctx);
	const principalId = body.machinePrincipalId.toLowerCase();
	if (externalIssuerSchema.safeParse(body.issuer).success) {
		if (!issuerNamesPrincipal(body.issuer, principalId)) ctx.addIssue({
			code: "custom",
			message: "a registered-key issuer must name machinePrincipalId",
			path: ["issuer"]
		});
		if (body.subject !== principalId) ctx.addIssue({
			code: "custom",
			message: "a registered-key policy must name machinePrincipalId, lower-case, as subject",
			path: ["subject"]
		});
		return;
	}
	if (Object.keys(body.requiredClaims).length === 0) {
		ctx.addIssue({
			code: "custom",
			message: EMPTY_CLAIMS_MESSAGE,
			path: ["requiredClaims"]
		});
		return;
	}
	if (isGitHubActionsIssuer(body.issuer) && !Object.hasOwn(body.requiredClaims, "job_workflow_ref")) ctx.addIssue({
		code: "custom",
		message: `a GitHub Actions policy must pin requiredClaims.${GITHUB_REQUIRED_CLAIM_KEY}`,
		path: ["requiredClaims"]
	});
});
/** `POST /v1/admin/machine-trust-policies` response: the created row, `disabled: false`. */
const createMachineTrustPolicyResponseSchema = adminMachineTrustPolicySchema;
/**
* `PATCH /v1/admin/machine-trust-policies/{id}` body: `{ disabled }`, required. `true` disables,
* `false` re-enables.
*
* Optional app binding (app-oidc, D9): `appId` and `appPermissions` together set or replace the
* row's binding, with the create body's rules, its 404 and its 400. Both or neither. The patch
* cannot remove a binding: an unbound row would mint the principal's whole role authority, so a
* wider policy is a new row.
*
* Every other key is refused, the match-key members included, because the match key is immutable.
*/
const updateMachineTrustPolicyRequestSchema = z.strictObject({
	disabled: z.boolean(),
	appId: z.uuid().optional(),
	appPermissions: trustPolicyAppPermissionsSchema.optional()
}).superRefine(refuseHalfBinding);
/** `PATCH /v1/admin/machine-trust-policies/{id}` response: the row after the change. */
const updateMachineTrustPolicyResponseSchema = adminMachineTrustPolicySchema;
/** Every route this file declares, for `route-census.ts`. */
const machineTrustPoliciesRoutes = Object.freeze([
	LIST_MACHINE_TRUST_POLICIES_ROUTE,
	CREATE_MACHINE_TRUST_POLICY_ROUTE,
	UPDATE_MACHINE_TRUST_POLICY_ROUTE
]);
//#endregion
export { ADMIN_MACHINE_TRUST_POLICIES_PATH, ADMIN_MACHINE_TRUST_POLICY_PATH_TEMPLATE, CREATE_MACHINE_TRUST_POLICY_ROUTE, GITHUB_ACTIONS_ISSUER, GITHUB_REQUIRED_CLAIM_KEY, LIST_MACHINE_TRUST_POLICIES_ROUTE, TRUST_POLICY_APP_PERMISSIONS_MAX, TRUST_POLICY_ISSUER_MAX_LENGTH, TRUST_POLICY_REQUIRED_CLAIMS_MAX_JSON_LENGTH, TRUST_POLICY_SUBJECT_MAX_LENGTH, UPDATE_MACHINE_TRUST_POLICY_ROUTE, adminMachineTrustPolicyPath, adminMachineTrustPolicySchema, createMachineTrustPolicyRequestSchema, createMachineTrustPolicyResponseSchema, listMachineTrustPoliciesResponseSchema, machineTrustPoliciesRoutes, trustPolicyAppPermissionsSchema, trustPolicyIssuerSchema, trustPolicyRequiredClaimsSchema, trustPolicySubjectSchema, updateMachineTrustPolicyRequestSchema, updateMachineTrustPolicyResponseSchema };

//# sourceMappingURL=machine-trust-policies.js.map